export class DealerMap {
	/*
	 * Set some defaults
	 */
	constructor() {
		// Defaults
		this.defaultZoomLevel = 5;
		this.focusZoomLevel = 10;
		this.activeBrand = 0;
		this.activeDealer = 0;
	}

	/*
	 * Init the block handler
	 *
	 * @param     {HTMLElement} rootEl          The root element of the block
	 * @param     {HTMLElement} mapEl           The element to inject a map instance into
	 * @param     {HTMLElement} selectEl        The brand picker element
	 * @param     {HTMLElement} cardEl          Dealer card element
	 * @param     {HTMLElement} cardTitleEl     Dealer card title element
	 * @param     {HTMLElement} addressEl       Dealer card address element
	 * @param     {HTMLElement} phoneEl         Dealer card phone element
	 * @param     {HTMLElement} emailEl         Dealer card email element
	 * @param     {HTMLElement} websiteEl       Dealer card website element
	 * @param     {HTMLElement} brandsListEl    Dealer card brands list element
	 * @return    {void}
	 */
	async init({
		rootEl,
		mapEl,
		selectEl,
		cardEl,
		cardTitleEl,
		addressEl,
		phoneEl,
		emailEl,
		websiteEl,
		brandsListEl,
	}) {
		// Store values
		this.mapEl = mapEl;
		this.rootEl = rootEl;
		this.selectEl = selectEl;
		this.cardEl = cardEl;
		this.cardTitleEl = cardTitleEl;
		this.addressEl = addressEl;
		this.phoneEl = phoneEl;
		this.emailEl = emailEl;
		this.websiteEl = websiteEl;
		this.brandsListEl = brandsListEl;

		// Populate dealer data, populate brand data, and init the map
		await Promise.all([
			this.fetchDealers(),
			this.fetchBrands(),
			this.initMap(),
		]).then((results) => {
			this.dealers = results[0];
			this.brands = results[1];
			this.map = results[2];
		});

		// Populate the dealer select
		this.populateBrandsSelect();

		// Create markers
		this.markers = this.createMapMarkers();

		// Reset marker icons and map zoom
		this.setActiveBrand(0);

		// Handle brand select changing
		this.selectEl.addEventListener(
			'change',
			this.onBrandSelectChange.bind(this)
		);

		// Handle clicking a dealer card brand
		this.brandsListEl.addEventListener(
			'click',
			this.onDealerBrandClicked.bind(this)
		);
	}

	/*
	 * Fetch, shape, and return dealer CPT data
	 *
	 * @return    {Promise<Array<any>>}
	 */
	async fetchDealers() {
		// Pull data from the REST API
		const restResponse = await window
			.fetch('/wp-json/wp/v2/dealers')
			.then((response) => response.json());

		// Shape the data
		const posts = restResponse.map((post) => {
			return (({
				address,
				brand_ids, // eslint-disable-line camelcase
				email,
				id,
				phone,
				slug,
				website,
				latlng,
			}) => {
				return {
					address,
					brandIds: brand_ids,
					email,
					id,
					phone,
					slug,
					website,
					latitude: latlng[0],
					longitude: latlng[1],
					title: post.title.rendered,
				};
			})(post);
		});

		// Done
		return posts;
	}

	/*
	 * Fetch, shape, and return brand CPT data
	 *
	 * @return    {Promise<Array<any>>}
	 */
	async fetchBrands() {
		// Pull data from the REST API
		const restResponse = await window
			.fetch('/wp-json/wp/v2/brand')
			.then((response) => response.json());

		// Shape the data
		const posts = restResponse.map((post) => {
			return (({ id, slug }) => {
				return {
					id,
					slug,
					title: post.title.rendered,
				};
			})(post);
		});

		// Done
		return posts;
	}

	/*
	 * Wait for the Maps API before initialising a maps instance
	 *
	 * @return    {Promise<Map>}
	 */
	async initMap() {
		// Wait for the maps API
		await this.pollMapsApi();

		// Create the map instance
		return new window.google.maps.Map(this.mapEl, this.getMapInitArgs());
	}

	/*
	 * Wait for the Google Maps API to init
	 *
	 * @return    {Promise<void>}
	 */
	pollMapsApi() {
		// Return polling promise
		return new Promise((resolve) => {
			// Looping callback used to check condition
			(function callback() {
				// If API loaded
				if ('undefined' !== typeof window.google) {
					return resolve();
				}

				// Queue the next callback loop
				window.requestAnimationFrame(callback);
			})();
		});
	}

	/*
	 * Return an object used to configure a new map instance
	 *
	 * @return    {Object}
	 */
	getMapInitArgs() {
		return {
			center: { lat: 51.5074, lng: 0.1278 },
			mapTypeControl: false,
			mapTypeId: window.google.maps.MapTypeId.ROADMAP,
			streetViewControl: false,
			// eslint-disable-next-line
			style: [{"elementType":"geometry","stylers":[{"color":"#f5f5f5"}]},{"elementType":"labels.icon","stylers":[{"visibility":"off"}]},{"elementType":"labels.text.fill","stylers":[{"color":"#616161"}]},{"elementType":"labels.text.stroke","stylers":[{"color":"#f5f5f5"}]},{"featureType":"administrative.land_parcel","elementType":"labels.text.fill","stylers":[{"color":"#bdbdbd"}]},{"featureType":"poi","elementType":"geometry","stylers":[{"color":"#eeeeee"}]},{"featureType":"poi","elementType":"labels.text.fill","stylers":[{"color":"#757575"}]},{"featureType":"poi.park","elementType":"geometry","stylers":[{"color":"#e5e5e5"}]},{"featureType":"poi.park","elementType":"labels.text.fill","stylers":[{"color":"#9e9e9e"}]},{"featureType":"road","elementType":"geometry","stylers":[{"color":"#ffffff"}]},{"featureType":"road.arterial","elementType":"labels.text.fill","stylers":[{"color":"#757575"}]},{"featureType":"road.highway","elementType":"geometry","stylers":[{"color":"#dadada"}]},{"featureType":"road.highway","elementType":"labels.text.fill","stylers":[{"color":"#616161"}]},{"featureType":"road.local","elementType":"labels.text.fill","stylers":[{"color":"#9e9e9e"}]},{"featureType":"transit.line","elementType":"geometry","stylers":[{"color":"#e5e5e5"}]},{"featureType":"transit.station","elementType":"geometry","stylers":[{"color":"#eeeeee"}]},{"featureType":"water","elementType":"geometry","stylers":[{"color":"#c9c9c9"}]},{"featureType":"water","elementType":"labels.text.fill","stylers":[{"color":"#9e9e9e"}]}],
			zoom: this.defaultZoomLevel,
		};
	}

	/*
	 * Populate the brand select
	 *
	 * @return    {void}
	 */
	populateBrandsSelect() {
		// Remove any existing (non-default) options
		const optionEls = Array.from(this.selectEl.querySelectorAll('option'));
		optionEls.forEach((optionEl) => {
			// Remove all but the default option
			if ('0' !== optionEl.getAttribute('value')) {
				optionEl.parentNode.removeChild(optionEl);
			}
		});

		// Create each new option
		const fragment = document.createDocumentFragment();
		this.brands
			.sort((a, b) => (a.title > b.title ? 1 : -1))
			.forEach((brand) => {
				// Create the empty element
				const optionEl = document.createElement('option');

				// Set values
				optionEl.setAttribute('value', String(brand.id));
				optionEl.innerText = brand.title;

				// Add to fragment
				fragment.appendChild(optionEl);
			});

		// Add all new options to the select
		this.selectEl.appendChild(fragment);
	}

	/*
	 * Create a map marker for each dealer
	 *
	 * @return    {Map<number, Marker>}
	 */
	createMapMarkers() {
		// Map to store markers against an ID
		const markers = new Map();

		// Create each marker
		this.dealers.forEach((dealer) => {
			// Basic config
			const markerConfig = {
				map: this.map,
				position: {
					lat: parseFloat(dealer.latitude),
					lng: parseFloat(dealer.longitude),
				},
				title: dealer.title,
			};

			// Create the marker
			const marker = new window.google.maps.Marker(markerConfig);

			// Handle clicking the marker
			marker.addListener('click', () => this.onMarkerClick(marker));

			// Store the marker
			markers.set(dealer.id, marker);
		});

		// Done
		return markers;
	}

	/*
	 * User has clicked a map marker
	 *
	 * @param     {Marker} marker    The clicked marker
	 * @return    {void}
	 */
	onMarkerClick(marker) {
		// Find the marker pair containing the clicked marker
		const markerPair = Array.from(this.markers.entries()).find((v) => {
			return v[1] === marker;
		});

		// Abort if no marker pair matched
		if ('undefined' === typeof markerPair) {
			return;
		}

		// Grab the dealer ID
		const dealerId = markerPair[0];
		const alreadySelected = dealerId === this.activeDealer;

		// Select or deselect
		if (alreadySelected) {
			this.setActiveDealer(0);
			this.zoomOnBrandMarkers();
		} else {
			this.setActiveDealer(dealerId);
			this.zoomOnSingleMarker(marker);
			this.updateCardInfo();
		}
	}

	/*
	 * User clicked a brand name on the dealer card
	 *
	 * @param     {Event} event    The event raised by the user action
	 * @return    {void}
	 */
	onDealerBrandClicked(event) {
		// Test the element
		if (
			!this.brandsListEl.contains(event.target) ||
			'undefined' === typeof event.target.dataset.brandId
		) {
			return;
		}

		// Set the active brand
		const brandId = parseInt(event.target.dataset.brandId, 10);
		this.setActiveBrand(brandId);
	}

	/*
	 * Brand select has changed
	 *
	 * @param     {Event} event    The event raised by the change
	 * @return    {void}
	 */
	onBrandSelectChange(event) {
		// Target must exist
		if (
			!(event.target instanceof window.HTMLElement) ||
			event.target !== this.selectEl
		) {
			return;
		}

		// Set the active brand
		const brandId = parseInt(this.selectEl.value, 10);
		this.setActiveBrand(brandId);
	}

	/*
	 * Set the active brand ID
	 *
	 * @param     {number} brandId    The active brand ID (or 0)
	 * @return    {void}
	 */
	setActiveBrand(brandId) {
		// Set the active brand
		this.activeBrand = brandId;

		// Clear the active dealer
		this.setActiveDealer(0);

		// Set the marker icons
		this.updateMarkerIcons();

		// Zoom the map
		this.zoomOnBrandMarkers();

		// Select the correct value in the brand select
		this.selectEl.value = brandId;
	}

	/*
	 * Set the active dealer ID
	 *
	 * @param     {number} dealerId    The active dealer ID (or 0)
	 * @return    {void}
	 */
	setActiveDealer(dealerId) {
		// Set the active dealer
		this.activeDealer = dealerId;

		// Set an attribute on the root el
		this.rootEl.dataset.activeDealer = dealerId;

		// Set the marker icons
		this.updateMarkerIcons();

		// Make sure the dealer card is visible
		if (1024 > window.innerWidth) {
			this.cardEl.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'nearest',
			});
		}
	}

	/*
	 * Update each markers icon to reflect the active brand and dealer IDs
	 *
	 * @return    {void}
	 */
	updateMarkerIcons() {
		// Fail if there are no markers
		if (!(this.markers instanceof Map)) {
			return;
		}

		// Choose an icon for each marker
		this.markers.forEach((marker, dealerId) => {
			// Find the dealer config object
			const dealer = this.dealers.find((o) => o.id === dealerId);
			if ('undefined' === typeof dealer) {
				return;
			}

			// Choose the marker
			if (this.activeDealer === dealerId) {
				marker.setIcon(
					'/wp-content/themes/source-distribution/images/pin-dealer.png'
				);
			} else if (
				0 === this.activeBrand ||
				dealer.brandIds.includes(this.activeBrand)
			) {
				marker.setIcon(
					'/wp-content/themes/source-distribution/images/pin-on.png'
				);
			} else {
				marker.setIcon(
					'/wp-content/themes/source-distribution/images/pin-removed.png'
				);
			}
		});
	}

	/*
	 * Zoom and centre all markers matching the currently active brand ID
	 *
	 * @return    {void}
	 */
	zoomOnBrandMarkers() {
		// Find active dealers
		let dealers = this.dealers.filter((dealer) => {
			return (
				this.markers.has(dealer.id) &&
				dealer.brandIds.includes(this.activeBrand)
			);
		});

		// Use all dealers if there are no matching ones
		if (0 === dealers.length) {
			dealers = this.dealers;
		}

		// Turn dealers into markers
		const markers = dealers.map((o) => this.markers.get(o.id));

		// Reposition and zoom the map to contain the markers
		switch (markers.length) {
			// Fail without markers
			case 0:
				return;

			// Explicitly focus lone markers
			case 1:
				this.map.setCenter(markers.find(() => true).getPosition());
				this.map.setZoom(this.focusZoomLevel);
				break;

			// Focus multiple markers
			default:
				const bounds = new window.google.maps.LatLngBounds();
				markers.forEach((o) => bounds.extend(o.getPosition()));
				this.map.fitBounds(bounds);
				this.map.setCenter(bounds.getCenter());
				break;
		}
	}

	/*
	 * Zoom and centre to a single marker
	 *
	 * @param     {Marker} marker    The marker to use
	 * @return    {void}
	 */
	zoomOnSingleMarker(marker) {
		this.map.setCenter(marker.getPosition());
		this.map.setZoom(this.focusZoomLevel);
	}

	/***/
	updateCardInfo() {
		// Find the active dealer
		const dealer = this.dealers.find((o) => o.id === this.activeDealer);
		if ('undefined' === typeof dealer) {
			return;
		}

		// Find all brands stocked by the dealer
		const brands = this.brands.filter((o) => {
			return dealer.brandIds.includes(o.id);
		});

		// Update the card title
		this.cardTitleEl.innerHTML = '';
		this.cardTitleEl.appendChild(document.createTextNode(dealer.title));

		// Update the address
		const haveAddress =
			'string' === typeof dealer.address && 0 < dealer.address.length;
		this.addressEl.style.display = haveAddress ? 'block' : 'none';
		if (haveAddress) {
			this.addressEl.innerHTML = '';
			this.addressEl.appendChild(document.createTextNode(dealer.address));
		}

		// Update the phone number
		const havePhone =
			'string' === typeof dealer.phone && 0 < dealer.phone.length;
		this.phoneEl.style.display = havePhone ? 'block' : 'none';
		if (havePhone) {
			const linkEl = this.phoneEl.querySelector('a');
			linkEl.setAttribute('href', `tel:${dealer.phone}`);
			linkEl.innerHTML = '';
			linkEl.appendChild(document.createTextNode(dealer.phone));
		}

		// Update the email address
		const haveEmail =
			'string' === typeof dealer.email && 0 < dealer.email.length;
		this.emailEl.style.display = haveEmail ? 'block' : 'none';
		if (haveEmail) {
			const linkEl = this.emailEl.querySelector('a');
			linkEl.setAttribute('href', `mailto:${dealer.email}`);
			linkEl.innerHTML = '';
			linkEl.appendChild(document.createTextNode(dealer.email));
		}

		// Update the website link
		const haveWebsite =
			'string' === typeof dealer.website && 0 < dealer.website.length;
		this.websiteEl.style.display = haveWebsite ? 'block' : 'none';
		if (haveWebsite) {
			const linkEl = this.websiteEl.querySelector('a');
			linkEl.setAttribute('href', dealer.website);
			linkEl.innerHTML = '';
			linkEl.appendChild(document.createTextNode(dealer.website));
		}

		// Empty the brands list
		while (this.brandsListEl.lastElementChild) {
			this.brandsListEl.removeChild(this.brandsListEl.lastElementChild);
		}

		// Repopulate the brands list
		const fragment = document.createDocumentFragment();
		brands.forEach((brand) => {
			const listEl = document.createElement('li');
			listEl.appendChild(document.createTextNode(brand.title));
			listEl.dataset.brandId = String(brand.id);

			fragment.appendChild(listEl);
		});
		this.brandsListEl.appendChild(fragment);
	}
}
